import React from 'react';

export default function TestCatalogPage() {
  const testCategories = [
    {
      category: "Drug Testing",
      tests: [
        { name: "DOT 5-Panel Drug Test", turnaround: "24-48 hours", description: "Federal DOT-mandated drug screening" },
        { name: "Non-DOT 10-Panel Drug Test", turnaround: "24-48 hours", description: "Comprehensive drug screening panel" },
        { name: "Urine Drug Screen", turnaround: "Same day", description: "Standard urine-based drug testing" },
      ]
    },
    {
      category: "DNA Testing",
      tests: [
        { name: "Paternity DNA Test", turnaround: "3-5 business days", description: "Legal and non-legal paternity testing" },
        { name: "Relationship DNA Test", turnaround: "5-7 business days", description: "Sibling, grandparent, and family relationship testing" },
        { name: "Immigration DNA Test", turnaround: "3-5 business days", description: "AABB-accredited immigration DNA testing" },
      ]
    },
    {
      category: "Blood Testing",
      tests: [
        { name: "Complete Blood Count (CBC)", turnaround: "24 hours", description: "Comprehensive blood cell analysis" },
        { name: "Metabolic Panel", turnaround: "24 hours", description: "Kidney and liver function assessment" },
        { name: "Lipid Panel", turnaround: "24 hours", description: "Cholesterol and triglyceride screening" },
        { name: "Hemoglobin A1C", turnaround: "24 hours", description: "Diabetes monitoring test" },
      ]
    },
    {
      category: "Infectious Disease",
      tests: [
        { name: "Flu Testing (Influenza A/B)", turnaround: "15-30 minutes", description: "Rapid influenza detection" },
        { name: "COVID-19 PCR Test", turnaround: "24-48 hours", description: "Molecular COVID-19 detection" },
        { name: "Strep Throat Test", turnaround: "15 minutes", description: "Rapid strep A detection" },
      ]
    },
    {
      category: "Background & Identity",
      tests: [
        { name: "Electronic Fingerprinting", turnaround: "Immediate submission", description: "FBI and state background checks" },
        { name: "I-9 Employment Verification", turnaround: "Same day", description: "Federal employment eligibility verification" },
      ]
    }
  ];

  return (
    <div className="bg-gray-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <nav className="flex text-sm" aria-label="Breadcrumb">
            <a href="/" className="text-[#4AB8D8] hover:text-[#F8A356]">Home</a>
            <span className="mx-2 text-gray-400">/</span>
            <span className="text-gray-600">Test Catalog</span>
          </nav>
        </div>
      </div>

      {/* Header */}
      <div className="bg-gradient-to-r from-[#4AB8D8] to-[#3A9AB8] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Test Catalog</h1>
          <p className="text-xl text-blue-100 max-w-3xl">
            Comprehensive laboratory testing services with fast turnaround times and accurate results
          </p>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            <input
              type="text"
              placeholder="Search tests by name or keyword..."
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4AB8D8] focus:border-transparent"
              aria-label="Search tests"
            />
            <button className="bg-[#4AB8D8] text-white px-8 py-3 rounded-lg hover:bg-[#3A9AB8] transition-colors font-semibold">
              Search
            </button>
          </div>
        </div>

        {/* Test Categories */}
        <div className="space-y-8">
          {testCategories.map((category, idx) => (
            <div key={idx} className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="bg-[#4AB8D8] text-white px-6 py-4">
                <h2 className="text-2xl font-bold">{category.category}</h2>
              </div>
              <div className="divide-y divide-gray-200">
                {category.tests.map((test, testIdx) => (
                  <div key={testIdx} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">{test.name}</h3>
                        <p className="text-gray-600 mb-2">{test.description}</p>
                        <div className="flex items-center gap-2 text-sm text-[#F8A356]">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="font-medium">Turnaround: {test.turnaround}</span>
                        </div>
                      </div>
                      <a
                        href="/appointment"
                        className="bg-[#F8A356] text-white px-6 py-3 rounded-lg hover:bg-[#3A9AB8] transition-colors font-semibold text-center whitespace-nowrap"
                      >
                        Schedule Test
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Additional Information */}
        <div className="mt-12 bg-[#4AB8D8]/10 rounded-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Important Testing Information</h2>
          <div className="grid md:grid-cols-2 gap-6 text-gray-700">
            <div>
              <h3 className="font-semibold text-lg mb-2 text-[#4AB8D8]">Preparation Guidelines</h3>
              <ul className="space-y-2 list-disc list-inside">
                <li>Fasting may be required for certain blood tests (8-12 hours)</li>
                <li>Bring valid photo identification</li>
                <li>Arrive 10 minutes before your scheduled appointment</li>
                <li>Inform staff of any medications you're currently taking</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-2 text-[#4AB8D8]">Results & Reports</h3>
              <ul className="space-y-2 list-disc list-inside">
                <li>Results available through secure online portal</li>
                <li>Physician notification for abnormal results</li>
                <li>Certified copies available upon request</li>
                <li>24/7 access to test history and records</li>
              </ul>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-[#4AB8D8] to-[#3A9AB8] rounded-lg p-8 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Need Help Choosing the Right Test?</h2>
          <p className="text-xl mb-6 text-white/90">Our experienced staff can guide you through the testing process</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/contact" className="bg-white text-[#4AB8D8] px-8 py-3 rounded-lg hover:bg-[#F8A356] hover:text-white transition-colors font-semibold">
              Contact Us
            </a>
            <a href="tel:9842309599" className="bg-[#3A9AB8] text-white px-8 py-3 rounded-lg hover:bg-[#F8A356] transition-colors font-semibold">
              Call (984) 230-9599
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
